// src/router.tsx
// React Router v7 router configuration using createBrowserRouter.
// Uses the loader/action pattern for data fetching and auth guards.

import { createBrowserRouter, Navigate } from 'react-router-dom'
import { RootLayout } from '@/layouts/RootLayout'
import { AuthLayout } from '@/layouts/AuthLayout'
import { authLoader, guestLoader } from '@/loaders/authLoader'
import { lazy, Suspense } from 'react'

// ── Lazy-loaded pages for code splitting ──────────────────────
const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage }))
)
const MembersPage = lazy(() =>
  import('@/pages/MembersPage').then((m) => ({ default: m.MembersPage }))
)
const ClassesPage = lazy(() =>
  import('@/pages/ClassesPage').then((m) => ({ default: m.ClassesPage }))
)
const EventsPage = lazy(() =>
  import('@/pages/EventsPage').then((m) => ({ default: m.EventsPage }))
)
const SermonsPage = lazy(() =>
  import('@/pages/SermonsPage').then((m) => ({ default: m.SermonsPage }))
)
const GivingPage = lazy(() =>
  import('@/pages/GivingPage').then((m) => ({ default: m.GivingPage }))
)
const SettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage }))
)
const DailyVersesPage = lazy(() =>
  import('@/pages/DailyVersesPage').then((m) => ({ default: m.DailyVersesPage }))
)
const AnnouncementsPage = lazy(() =>
  import('@/pages/AnnouncementsPage').then((m) => ({ default: m.AnnouncementsPage }))
)
const EventTypesPage = lazy(() =>
  import('@/pages/EventTypesPage').then((m) => ({ default: m.EventTypesPage }))
)
const NewsPage = lazy(() =>
  import('@/pages/NewsPage').then((m) => ({ default: m.NewsPage }))
)
const LoginPage = lazy(() =>
  import('@/pages/LoginPage').then((m) => ({ default: m.LoginPage }))
)
const AttendancePage = lazy(() =>
  import('@/pages/AttendancePage').then((m) => ({ default: m.default }))
)
const PrayerRequestsPage = lazy(() =>
  import('@/pages/PrayerRequestsPage').then((m) => ({ default: m.PrayerRequestsPage }))
)
const TestimoniesPage = lazy(() =>
  import('@/pages/TestimoniesPage').then((m) => ({ default: m.TestimoniesPage }))
)
const GratiaFamiliesPage = lazy(() =>
  import('@/pages/GratiaFamiliesPage').then((m) => ({ default: m.GratiaFamiliesPage }))
)
const GratiaFamilyDetailPage = lazy(() =>
  import('@/pages/GratiaFamilyDetailPage').then((m) => ({ default: m.GratiaFamilyDetailPage }))
)
const OurServicesPage = lazy(() =>
  import('@/pages/OurServicesPage').then((m) => ({ default: m.OurServicesPage }))
)
const BlessingMarketPage = lazy(() =>
  import('@/pages/BlessingMarketPage')
)
const ProfilePage = lazy(() =>
  import('@/pages/ProfilePage').then((m) => ({ default: m.ProfilePage }))
)
const PrivacyPolicyPage = lazy(() =>
  import('@/pages/PrivacyPolicyPage').then((m) => ({ default: m.PrivacyPolicyPage }))
)


// ── Page loading fallback ─────────────────────────────────────
function PageLoader() {
  return (
    <div className="flex items-center justify-center h-full min-h-[400px]">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    </div>
  )
}

// ── Router ────────────────────────────────────────────────────

export const router = createBrowserRouter([
  // ── Public routes ──────────────────────────────────────────
  {
    path: '/privacy_policy',
    element: (
      <Suspense fallback={<PageLoader />}>
        <PrivacyPolicyPage />
      </Suspense>
    ),
  },
  {
    path: '/privacy',
    element: (
      <Suspense fallback={<PageLoader />}>
        <PrivacyPolicyPage />
      </Suspense>
    ),
  },

  // ── Protected routes (require auth) ──────────────────────
  {
    path: '/',
    element: <RootLayout />,
    loader: authLoader,
    errorElement: (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md p-8">
          <div className="text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Something went wrong</h1>
          <p className="text-muted-foreground mb-6">
            An unexpected error occurred. Please refresh the page.
          </p>
          <button
            onClick={() => window.location.replace('/')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageLoader />}>
            <DashboardPage />
          </Suspense>
        ),
      },
      {
        path: 'classes',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ClassesPage />
          </Suspense>
        ),
      },
      {
        path: 'members',
        element: (
          <Suspense fallback={<PageLoader />}>
            <MembersPage />
          </Suspense>
        ),
      },
      {
        path: 'events',
        element: (
          <Suspense fallback={<PageLoader />}>
            <EventsPage />
          </Suspense>
        ),
      },
      {
        path: 'sermons',
        element: (
          <Suspense fallback={<PageLoader />}>
            <SermonsPage />
          </Suspense>
        ),
      },
      {
        path: 'giving',
        element: (
          <Suspense fallback={<PageLoader />}>
            <GivingPage />
          </Suspense>
        ),
      },
      {
        path: 'settings',
        element: (
          <Suspense fallback={<PageLoader />}>
            <SettingsPage />
          </Suspense>
        ),
      },
      {
        path: 'daily-verses',
        element: (
          <Suspense fallback={<PageLoader />}>
            <DailyVersesPage />
          </Suspense>
        ),
      },
      {
        path: 'announcements',
        element: (
          <Suspense fallback={<PageLoader />}>
            <AnnouncementsPage />
          </Suspense>
        ),
      },
      {
        path: 'event-types',
        element: (
          <Suspense fallback={<PageLoader />}>
            <EventTypesPage />
          </Suspense>
        ),
      },
      {
        path: 'news',
        element: (
          <Suspense fallback={<PageLoader />}>
            <NewsPage />
          </Suspense>
        ),
      },
      {
        path: 'attendance',
        element: (
          <Suspense fallback={<PageLoader />}>
            <AttendancePage />
          </Suspense>
        ),
      },
      {
        path: 'prayers',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PrayerRequestsPage />
          </Suspense>
        ),
      },
      {
        path: 'testimonies',
        element: (
          <Suspense fallback={<PageLoader />}>
            <TestimoniesPage />
          </Suspense>
        ),
      },
      {
        path: 'gratia-families',
        element: (
          <Suspense fallback={<PageLoader />}>
            <GratiaFamiliesPage />
          </Suspense>
        ),
      },
      {
        path: 'gratia-families/:id',
        element: (
          <Suspense fallback={<PageLoader />}>
            <GratiaFamilyDetailPage />
          </Suspense>
        ),
      },
      {
        path: 'our-services',
        element: (
          <Suspense fallback={<PageLoader />}>
            <OurServicesPage />
          </Suspense>
        ),
      },
      {
        path: 'blessing-market',
        element: (
          <Suspense fallback={<PageLoader />}>
            <BlessingMarketPage />
          </Suspense>
        ),
      },
      {
        path: 'profile',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProfilePage />
          </Suspense>
        ),
      },
    ],
  },

  // ── Auth routes (redirect if already logged in) ───────────
  {
    path: '/auth',
    element: <AuthLayout />,
    loader: guestLoader,
    children: [
      {
        index: true,
        element: <Navigate to="/auth/login" replace />,
      },
      {
        path: 'login',
        element: (
          <Suspense fallback={<PageLoader />}>
            <LoginPage />
          </Suspense>
        ),
      },
      // OAuth callback — handled by Supabase detectSessionInUrl
      {
        path: 'callback',
        element: (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">Completing sign in...</p>
            </div>
          </div>
        ),
      },
    ],
  },

  // ── 404 catch-all ─────────────────────────────────────────
  {
    path: '*',
    element: (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md p-8">
          <div className="text-8xl font-bold text-primary/20 mb-4">404</div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Page not found</h1>
          <p className="text-muted-foreground mb-6">
            The page you are looking for does not exist.
          </p>
          <a
            href="/"
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Back to Dashboard
          </a>
        </div>
      </div>
    ),
  },
])
